/**
 * Register Page
 */

import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, TrendingUp, Lock, User, Mail, CheckCircle, Ticket, AlertTriangle, ShieldCheck } from 'lucide-react';
import PasswordStrength, { isPasswordAcceptable } from '../components/PasswordStrength';
import TwoFactorSetup from '../components/TwoFactorSetup';

const Register = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || '';
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [enrollToken, setEnrollToken] = useState('');
  const { register, applySession, error, clearError } = useAuth();

  const pwStrong = isPasswordAcceptable(password, [username, email]);
  const passwordRequirements = [
    { met: password.length >= 10, text: 'At least 10 characters' },
    { met: pwStrong, text: 'Strong enough (not a common/guessable password)' },
    { met: password === confirmPassword && password.length > 0, text: 'Passwords match' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!username || !password) {
      setLocalError('Username and password are required');
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (!pwStrong) {
      setLocalError('Please choose a stronger password (at least 10 characters, not a common one).');
      return;
    }

    setLoading(true);
    const res = await register(username, password, email || undefined, inviteToken);
    setLoading(false);
    if (res.status === 'enroll') setEnrollToken(res.mfaToken);
    // 'ok' → app redirects; 'error' → shown via context error.
  };

  const displayError = localError || error;

  // After a successful signup, mandatory 2FA enrollment before the account is usable.
  if (enrollToken) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl gradient-bg-blue mb-4">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-display text-3xl font-bold gradient-text-blue">Paeru Exchange</h1>
            <p className="text-text-secondary mt-2">Account created — one last step</p>
          </div>
          <div className="card p-8">
            <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-5 h-5 text-accent-purple" /><h2 className="font-display text-2xl font-semibold">Set up two-factor</h2></div>
            <p className="text-text-secondary text-sm mb-6">Two-factor authentication is required to protect your account.</p>
            <TwoFactorSetup authToken={enrollToken} onComplete={(session) => { if (session) applySession(session); }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl gradient-bg-blue mb-4">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-display text-3xl font-bold gradient-text-blue">Paeru Exchange</h1>
          <p className="text-text-secondary mt-2">Create your trading account</p>
          <p className="text-text-muted text-xs mt-1">פארו • Aramaic for "Money"</p>
        </div>

        {/* Register Form */}
        <div className="card p-8">
          <h2 className="font-display text-2xl font-semibold mb-6">Get Started</h2>

          {inviteToken ? (
            <div className="mb-6 p-3 rounded-lg bg-accent-green/10 border border-accent-green/20 flex items-center gap-2">
              <Ticket className="w-4 h-4 text-accent-green flex-none" />
              <p className="text-accent-green text-sm">You've been invited — complete the form to join.</p>
            </div>
          ) : (
            <div className="mb-6 p-4 rounded-lg bg-accent-yellow/10 border border-accent-yellow/20 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-accent-yellow flex-none mt-0.5" />
              <p className="text-accent-yellow text-sm">Paeru is invite-only. You need an invite link from an administrator to create an account.</p>
            </div>
          )}

          {displayError && (
            <div className="mb-6 p-4 rounded-lg bg-accent-red/10 border border-accent-red/20">
              <p className="text-accent-red text-sm">{displayError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-5">
              <label className="block text-text-secondary text-sm mb-2">Username *</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); clearError(); setLocalError(''); }}
                  placeholder="Choose a username"
                  className="input input-with-icon"
                  autoComplete="username"
                  minLength={3}
                />
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-text-secondary text-sm mb-2">
                Email <span className="text-text-muted">(optional)</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="input input-with-icon"
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-text-secondary text-sm mb-2">Password *</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearError(); setLocalError(''); }}
                  placeholder="Create a password"
                  className="input input-with-icon input-with-right-icon"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <PasswordStrength password={password} userInputs={[username, email]} />
            </div>

            <div className="mb-5">
              <label className="block text-text-secondary text-sm mb-2">Confirm Password *</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setLocalError(''); }}
                  placeholder="Confirm your password"
                  className="input input-with-icon"
                  autoComplete="new-password"
                />
              </div>
            </div>

            {/* Password requirements */}
            <div className="mb-6 space-y-2">
              {passwordRequirements.map((req, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <CheckCircle className={`w-4 h-4 ${req.met ? 'text-accent-green' : 'text-text-muted'}`} />
                  <span className={req.met ? 'text-accent-green' : 'text-text-muted'}>{req.text}</span>
                </div>
              ))}
            </div>

            <button
              type="submit"
              disabled={loading || !inviteToken || !username || !pwStrong || password !== confirmPassword}
              className="w-full btn-primary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-text-secondary">
            Already have an account?{' '}
            <Link to="/login" className="text-accent-blue hover:underline">
              Sign in
            </Link>
          </p>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-text-muted text-sm">
          Paper trading only. Your credentials are encrypted and never shared.
        </p>
      </div>
    </div>
  );
};

export default Register;
